import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useBrandingColors } from '../hooks/useBrandingColors';
import {
  deleteAtsIntegration,
  getAtsIntegrations,
  getEstaffCandidateSources,
  getEstaffCompanySettings,
  getEstaffEventTypes,
  saveAtsIntegration,
  saveEstaffCompanySettings,
  type EstaffCompanySettings,
  type EstaffStatusAction,
  type EstaffStatusMapping
} from '../relevanter/services/api';
import { useAuthStore } from '../store/authStore';

type AtsSource = 'estaff' | 'skillaz';

type AtsConfiguration = {
  id: AtsSource;
  name: string;
  configured: boolean;
  apiUrl: string;
  updatedAt: string | null;
  atsInstanceId?: number | null;
  mirrorReady?: boolean | null;
  mirrorPhase?: string | null;
  mirrorSyncEnabled?: boolean | null;
  mirrorLastSyncedAt?: string | null;
  settingsReady?: boolean | null;
  settingsPhase?: string | null;
  settingsCounts?: Record<string, unknown> | null;
};

type CandidateSourceOption = {
  id: string;
  name: string;
};

type CandidateSourcesResponse = CandidateSourceOption[] | {
  items?: CandidateSourceOption[];
};

type EventTypeOption = {
  id: string;
  name: string;
  stateId?: string;
  extra?: Record<string, unknown>;
};

type EventTypesResponse = EventTypeOption[] | {
  items?: EventTypeOption[];
};

type StatusMappingKey = keyof EstaffStatusMapping;

const EMPTY_STATUS_MAPPING: EstaffStatusMapping = {
  contactEstablished: '',
  hrInterviewScheduled: '',
  aiScreeningPassed: null,
};
const ACTIVE_EVENT_TYPE_FLAGS = ['is_active', 'isActive', 'active', 'enabled', 'visible'];
const INACTIVE_EVENT_TYPE_FLAGS = ['disabled', 'is_disabled', 'inactive', 'is_deleted', 'isDeleted', 'deleted', 'archived'];

const STATUS_ACTION_ROWS: Array<{ key: StatusMappingKey; label: string; noStatusLabel?: string }> = [
  { key: 'contactEstablished', label: 'Старт диалога / первый контакт' },
  { key: 'hrInterviewScheduled', label: 'Назначение HR-интервью' },
  { key: 'aiScreeningPassed', label: 'Завершение AI-скрининга', noStatusLabel: 'Не менять статус' },
];
const DEFAULT_CANDIDATE_SOURCE_LABEL = 'По умолчанию (Релевантер)';

const EMPTY_CONFIGS: Record<AtsSource, AtsConfiguration> = {
  estaff: {
    id: 'estaff',
    name: 'E-Staff',
    configured: false,
    apiUrl: '',
    updatedAt: null,
    atsInstanceId: null,
    mirrorReady: null,
    mirrorPhase: null,
    mirrorSyncEnabled: null,
    mirrorLastSyncedAt: null,
    settingsReady: null,
    settingsPhase: null,
    settingsCounts: null
  },
  skillaz: {
    id: 'skillaz',
    name: 'Skillaz',
    configured: false,
    apiUrl: '',
    updatedAt: null,
    atsInstanceId: null,
    mirrorReady: null,
    mirrorPhase: null,
    mirrorSyncEnabled: null,
    mirrorLastSyncedAt: null,
    settingsReady: null,
    settingsPhase: null,
    settingsCounts: null
  }
};

function normalizeCandidateSources(response: CandidateSourcesResponse | null | undefined): CandidateSourceOption[] {
  const items = Array.isArray(response) ? response : response?.items || [];
  return items.filter((item) => Boolean(item?.id && item?.name));
}

function parseEventTypeBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'да'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n', 'нет'].includes(normalized)) return false;
  }
  return undefined;
}

function readEventTypeFlag(item: EventTypeOption, flagName: string): unknown {
  const record = item as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(record, flagName)) {
    return record[flagName];
  }
  if (item.extra && Object.prototype.hasOwnProperty.call(item.extra, flagName)) {
    return item.extra[flagName];
  }
  return undefined;
}

function readFirstEventTypeFlag(item: EventTypeOption, flagNames: string[]): boolean | undefined {
  for (const flagName of flagNames) {
    const parsed = parseEventTypeBoolean(readEventTypeFlag(item, flagName));
    if (parsed !== undefined) return parsed;
  }
  return undefined;
}

function normalizeEventTypes(response: EventTypesResponse | null | undefined): EventTypeOption[] {
  const items = Array.isArray(response) ? response : response?.items || [];
  return items.map((item) => {
    const id = item?.id?.trim() || '';
    const name = item?.name?.trim() || '';
    const activeFlag = readFirstEventTypeFlag(item, ACTIVE_EVENT_TYPE_FLAGS);
    const inactiveFlag = readFirstEventTypeFlag(item, INACTIVE_EVENT_TYPE_FLAGS);
    const isSelectable = Boolean(id && name)
      && activeFlag !== false
      && inactiveFlag !== true;
    if (!isSelectable) return null;
    const stateId = item.stateId?.trim();
    return {
      ...item,
      id,
      name,
      ...(stateId ? { stateId } : {}),
    };
  }).filter((item): item is EventTypeOption => item !== null);
}

function resolveStatusMapping(settings: EstaffCompanySettings | null | undefined): EstaffStatusMapping {
  if (!settings?.exists) {
    return EMPTY_STATUS_MAPPING;
  }

  return {
    contactEstablished: settings?.statusMapping?.contactEstablished
      || settings?.contactEstablishedStatusId
      || '',
    hrInterviewScheduled: settings?.statusMapping?.hrInterviewScheduled
      || settings?.hrInterviewStatusId
      || '',
    aiScreeningPassed: settings?.statusMapping?.aiScreeningPassed
      || settings?.screeningStatusId
      || null,
  };
}

function areStatusMappingsEqual(left: EstaffStatusMapping, right: EstaffStatusMapping): boolean {
  return STATUS_ACTION_ROWS.every(({ key }) => left[key] === right[key]);
}

function getEventTypeOptionValue(eventType: EventTypeOption): string {
  return eventType.stateId || eventType.id;
}

function normalizeStatusMappingForEventTypes(
  mapping: EstaffStatusMapping,
  eventTypes: EventTypeOption[]
): EstaffStatusMapping {
  const valuesByEventTypeId = new Map(
    eventTypes.map((eventType) => [eventType.id, getEventTypeOptionValue(eventType)])
  );
  const normalizeValue = (value: string | null): string | null => (
    value && !value.includes(':') && valuesByEventTypeId.has(value)
      ? valuesByEventTypeId.get(value)!
      : value
  );
  return {
    contactEstablished: normalizeValue(mapping.contactEstablished) || '',
    hrInterviewScheduled: normalizeValue(mapping.hrInterviewScheduled) || '',
    aiScreeningPassed: normalizeValue(mapping.aiScreeningPassed),
  };
}

function normalizeStatusActions(settings: EstaffCompanySettings | null | undefined): EstaffStatusAction[] {
  const actionsByKey = new Map((settings?.statusActions || []).map((action) => [action.key, action]));
  return STATUS_ACTION_ROWS.map((row) => actionsByKey.get(row.key) || {
    key: row.key,
    action: row.key,
    label: row.label,
    statusId: settings?.statusMapping?.[row.key] || null,
    stateId: settings?.statusMapping?.[row.key] || null,
    stateName: null,
    changesStatus: Boolean(settings?.statusMapping?.[row.key]),
    allowNoStatus: row.key === 'aiScreeningPassed',
    noStatusLabel: row.noStatusLabel || null,
    settingsSource: settings?.exists ? 'settings' : 'default',
    dictionaryAvailable: false,
  });
}

function getStatusOptionLabel(eventType: EventTypeOption): string {
  const value = getEventTypeOptionValue(eventType);
  return `${eventType.name} — ${value}`;
}

function getSelectedStatusLabel(
  row: { key: StatusMappingKey; noStatusLabel?: string },
  selectedStatusId: string,
  eventTypes: EventTypeOption[],
  statusAction?: EstaffStatusAction
): string {
  if (!selectedStatusId) {
    return row.key === 'aiScreeningPassed' ? (row.noStatusLabel || 'Не менять статус') : 'Статус не выбран';
  }
  const selectedOption = eventTypes.find((eventType) => getEventTypeOptionValue(eventType) === selectedStatusId);
  const selectedName = statusAction?.stateId === selectedStatusId
    ? statusAction?.stateName
    : selectedOption?.name;
  return selectedName ? `${selectedName} — ${selectedStatusId}` : selectedStatusId;
}

const ATSIntegrationContent: React.FC = () => {
  const colors = useBrandingColors();
  const companyId = useAuthStore(state => state.user?.companyId || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [selectedSource, setSelectedSource] = useState<AtsSource>('estaff');
  const [configurations, setConfigurations] = useState<Record<AtsSource, AtsConfiguration>>(EMPTY_CONFIGS);
  const [candidateSources, setCandidateSources] = useState<CandidateSourceOption[]>([]);
  const [candidateSourcesLoading, setCandidateSourcesLoading] = useState(false);
  const [candidateSourcesLoadedForCompanyId, setCandidateSourcesLoadedForCompanyId] = useState<number | null>(null);
  const [candidateSourceId, setCandidateSourceId] = useState('');
  const [savedCandidateSourceId, setSavedCandidateSourceId] = useState('');
  const [candidateSourceTouched, setCandidateSourceTouched] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [statusMapping, setStatusMapping] = useState<EstaffStatusMapping>(EMPTY_STATUS_MAPPING);
  const [savedStatusMapping, setSavedStatusMapping] = useState<EstaffStatusMapping>(EMPTY_STATUS_MAPPING);
  const [statusActions, setStatusActions] = useState<EstaffStatusAction[]>([]);
  const [form, setForm] = useState({
    apiUrl: '',
    apiToken: ''
  });

  const currentConfig = useMemo(
    () => configurations[selectedSource] || EMPTY_CONFIGS[selectedSource],
    [configurations, selectedSource]
  );

  const loadConfigurations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAtsIntegrations();
      const nextConfigurations: Record<AtsSource, AtsConfiguration> = {
        estaff: {
          id: 'estaff',
          name: 'E-Staff',
          configured: !!response?.configurations?.estaff?.configured,
          apiUrl: response?.configurations?.estaff?.apiUrl || '',
          updatedAt: response?.configurations?.estaff?.updatedAt || null,
          atsInstanceId: response?.configurations?.estaff?.atsInstanceId ?? null,
          mirrorReady: typeof response?.configurations?.estaff?.mirrorReady === 'boolean'
            ? response.configurations.estaff.mirrorReady
            : null,
          mirrorPhase: response?.configurations?.estaff?.mirrorPhase || null,
          mirrorSyncEnabled: typeof response?.configurations?.estaff?.mirrorSyncEnabled === 'boolean'
            ? response.configurations.estaff.mirrorSyncEnabled
            : null,
          mirrorLastSyncedAt: response?.configurations?.estaff?.mirrorLastSyncedAt || null,
          settingsReady: typeof response?.configurations?.estaff?.settingsReady === 'boolean'
            ? response.configurations.estaff.settingsReady
            : null,
          settingsPhase: response?.configurations?.estaff?.settingsPhase || null,
          settingsCounts: response?.configurations?.estaff?.settingsCounts || null
        },
        skillaz: {
          id: 'skillaz',
          name: 'Skillaz',
          configured: !!response?.configurations?.skillaz?.configured,
          apiUrl: response?.configurations?.skillaz?.apiUrl || '',
          updatedAt: response?.configurations?.skillaz?.updatedAt || null,
          atsInstanceId: response?.configurations?.skillaz?.atsInstanceId ?? null,
          mirrorReady: typeof response?.configurations?.skillaz?.mirrorReady === 'boolean'
            ? response.configurations.skillaz.mirrorReady
            : null,
          mirrorPhase: response?.configurations?.skillaz?.mirrorPhase || null,
          mirrorSyncEnabled: typeof response?.configurations?.skillaz?.mirrorSyncEnabled === 'boolean'
            ? response.configurations.skillaz.mirrorSyncEnabled
            : null,
          mirrorLastSyncedAt: response?.configurations?.skillaz?.mirrorLastSyncedAt || null,
          settingsReady: typeof response?.configurations?.skillaz?.settingsReady === 'boolean'
            ? response.configurations.skillaz.settingsReady
            : null,
          settingsPhase: response?.configurations?.skillaz?.settingsPhase || null,
          settingsCounts: response?.configurations?.skillaz?.settingsCounts || null
        }
      };
      setConfigurations(nextConfigurations);
    } catch (error) {
      toast.error('Не удалось загрузить настройки ATS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfigurations();
  }, [loadConfigurations]);

  useEffect(() => {
    setForm({
      apiUrl: configurations[selectedSource]?.apiUrl || '',
      apiToken: ''
    });
  }, [selectedSource, configurations]);

  const estaffConfigured = configurations.estaff.configured;
  const estaffMirrorReady = configurations.estaff.mirrorReady;
  const estaffSettingsReady = configurations.estaff.settingsReady;
  const estaffSettingsUnavailable = estaffConfigured
    && (estaffSettingsReady === false || (estaffSettingsReady !== true && estaffMirrorReady === false));
  useEffect(() => {
    let isActive = true;

    if (selectedSource !== 'estaff') {
      setCandidateSourcesLoading(false);
      setEventTypesLoading(false);
      return () => {
        isActive = false;
      };
    }

    if (!companyId || !estaffConfigured || estaffSettingsUnavailable) {
      setCandidateSources([]);
      setCandidateSourcesLoadedForCompanyId(null);
      setCandidateSourceId('');
      setSavedCandidateSourceId('');
      setCandidateSourceTouched(false);
      setEventTypes([]);
      setStatusMapping(EMPTY_STATUS_MAPPING);
      setSavedStatusMapping(EMPTY_STATUS_MAPPING);
      setStatusActions([]);
      setCandidateSourcesLoading(false);
      setEventTypesLoading(false);
      return () => {
        isActive = false;
      };
    }

    if (candidateSourcesLoadedForCompanyId === companyId) {
      return () => {
        isActive = false;
      };
    }

    const loadCandidateSources = async () => {
      setCandidateSourcesLoading(true);
      setEventTypesLoading(true);
      try {
        const [sourcesResponse, eventTypesResponse] = await Promise.all([
          getEstaffCandidateSources(companyId),
          getEstaffEventTypes(companyId)
        ]);
        const settings = await getEstaffCompanySettings(companyId);

        if (!isActive) return;

        const sources = normalizeCandidateSources(sourcesResponse);
        const nextEventTypes = normalizeEventTypes(eventTypesResponse);
        const savedSourceId = settings?.sourceId || '';
        const nextStatusMapping = normalizeStatusMappingForEventTypes(
          resolveStatusMapping(settings),
          nextEventTypes
        );
        setCandidateSources(sources);
        setEventTypes(nextEventTypes);
        setStatusActions(normalizeStatusActions(settings));
        setCandidateSourcesLoadedForCompanyId(companyId);
        setCandidateSourceId(savedSourceId);
        setSavedCandidateSourceId(savedSourceId);
        setCandidateSourceTouched(false);
        setStatusMapping(nextStatusMapping);
        setSavedStatusMapping(nextStatusMapping);
      } catch (error) {
        if (!isActive) return;
        setCandidateSources([]);
        setEventTypes([]);
        setStatusActions([]);
        setCandidateSourcesLoadedForCompanyId(null);
        setCandidateSourceId('');
        setSavedCandidateSourceId('');
        setCandidateSourceTouched(false);
        setStatusMapping(EMPTY_STATUS_MAPPING);
        setSavedStatusMapping(EMPTY_STATUS_MAPPING);
        toast.error('Не удалось загрузить настройки E-Staff');
      } finally {
        if (isActive) {
          setCandidateSourcesLoading(false);
          setEventTypesLoading(false);
        }
      }
    };

    void loadCandidateSources();

    return () => {
      isActive = false;
    };
  }, [selectedSource, companyId, estaffConfigured, estaffSettingsUnavailable, candidateSourcesLoadedForCompanyId]);

  const credentialsReady = Boolean(form.apiUrl.trim() && form.apiToken.trim());
  const sourceSettingsDirty = selectedSource === 'estaff'
    && currentConfig.configured
    && !candidateSourcesLoading
    && candidateSourceId !== savedCandidateSourceId;
  const statusSettingsDirty = selectedSource === 'estaff'
    && currentConfig.configured
    && !eventTypesLoading
    && !areStatusMappingsEqual(statusMapping, savedStatusMapping);
  const canSave = credentialsReady || sourceSettingsDirty || statusSettingsDirty;
  const isSaveDisabled = saving || disconnecting || !canSave;
  const estaffMirrorPending = selectedSource === 'estaff'
    && currentConfig.configured
    && currentConfig.mirrorReady === false;
  const estaffMirrorAwaitingAdmin = estaffMirrorPending
    && currentConfig.mirrorPhase === 'mirror_disabled';
  const estaffMirrorPendingMessage = estaffMirrorAwaitingAdmin
    ? 'Ожидайте подтверждения администратором: интеграция подключена, зеркало данных ещё не включено.'
    : currentConfig.settingsReady === true
      ? 'Зеркало данных ещё готовится. Поиск кандидатов станет доступен после синхронизации.'
      : 'Зеркало данных ещё готовится. Настройки E-Staff станут доступны после синхронизации.';
  const estaffSettingsBlocked = selectedSource === 'estaff'
    && currentConfig.configured
    && (
      currentConfig.settingsReady === false
      || (currentConfig.settingsReady !== true && currentConfig.mirrorReady === false)
    );
  const estaffSettingsBlockedLabel = estaffMirrorAwaitingAdmin
    ? 'Ожидайте подтверждения администратором'
    : 'Зеркало данных ещё готовится';

  const handleSave = async () => {
    const shouldSaveCredentials = credentialsReady;
    const shouldSaveSourceSettings = sourceSettingsDirty && (!shouldSaveCredentials || candidateSourceTouched);
    const shouldSaveEstaffSettings = sourceSettingsDirty || statusSettingsDirty;
    const shouldPersistEstaffSettings = shouldSaveSourceSettings || statusSettingsDirty;

    if (!shouldSaveCredentials && !shouldSaveEstaffSettings) {
      toast.error(currentConfig.configured ? 'Нет изменений для сохранения' : 'Заполните адрес и токен');
      return;
    }
    if (shouldPersistEstaffSettings && !companyId) {
      toast.error('Не удалось определить компанию');
      return;
    }

    setSaving(true);
    try {
      if (shouldSaveCredentials) {
        await saveAtsIntegration({
          source: selectedSource,
          apiUrl: form.apiUrl.trim(),
          apiToken: form.apiToken.trim()
        });
      }

      if (shouldPersistEstaffSettings && companyId) {
        const payload: Partial<Pick<EstaffCompanySettings, 'sourceId' | 'statusMapping'>> = {};
        if (shouldSaveSourceSettings) {
          payload.sourceId = candidateSourceId || null;
        }
        if (statusSettingsDirty) {
          payload.statusMapping = statusMapping;
        }
        const settings = await saveEstaffCompanySettings(companyId, payload);
        const nextSourceId = settings?.sourceId || '';
        const nextStatusMapping = normalizeStatusMappingForEventTypes(
          resolveStatusMapping(settings),
          eventTypes
        );
        setStatusActions(normalizeStatusActions(settings));
        setCandidateSourceId(nextSourceId);
        setSavedCandidateSourceId(nextSourceId);
        setCandidateSourceTouched(false);
        setStatusMapping(nextStatusMapping);
        setSavedStatusMapping(nextStatusMapping);
      }

      if (shouldSaveCredentials && selectedSource === 'estaff') {
        setCandidateSources([]);
        setCandidateSourcesLoadedForCompanyId(null);
        setCandidateSourceId('');
        setSavedCandidateSourceId('');
        setCandidateSourceTouched(false);
        setEventTypes([]);
        setStatusActions([]);
        setStatusMapping(EMPTY_STATUS_MAPPING);
        setSavedStatusMapping(EMPTY_STATUS_MAPPING);
      }

      toast.success(shouldSaveCredentials ? 'Интеграция подключена' : 'Настройки E-Staff сохранены');
      if (shouldSaveCredentials) {
        await loadConfigurations();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Не удалось подключиться, обратитесь в поддержку');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!currentConfig.configured) {
      return;
    }
    const confirmed = window.confirm(
      `Отключить интеграцию ${currentConfig.name}? Текущие настройки будут удалены.`
    );
    if (!confirmed) {
      return;
    }

    setDisconnecting(true);
    try {
      await deleteAtsIntegration(selectedSource);
      toast.success('Интеграция отключена');
      setForm({ apiUrl: '', apiToken: '' });
      setCandidateSources([]);
      setCandidateSourcesLoadedForCompanyId(null);
      setCandidateSourceId('');
      setSavedCandidateSourceId('');
      setCandidateSourceTouched(false);
      setEventTypes([]);
      setStatusActions([]);
      setStatusMapping(EMPTY_STATUS_MAPPING);
      setSavedStatusMapping(EMPTY_STATUS_MAPPING);
      await loadConfigurations();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Не удалось отключить интеграцию');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: colors.primary }}></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  const hasSelectedSourceOption = Boolean(
    candidateSourceId && candidateSources.some((source) => source.id === candidateSourceId)
  );
  const eventTypeValues = new Set(eventTypes.map(getEventTypeOptionValue));
  const statusSelectDisabled = !currentConfig.configured || estaffSettingsBlocked || eventTypesLoading || eventTypes.length === 0;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">ATS интеграция</h3>
            <p className="mt-1 text-sm text-gray-600">
              Подключите E-Staff или Skillaz по адресу API и токену доступа
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Для полной интеграции с E-Staff ваш логин в E-Staff должен совпадать с email в системе
            </p>
            <div className="mt-3 flex items-center gap-2">
              {currentConfig.configured ? (
                <>
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">
                    {currentConfig.name} подключен
                  </span>
                </>
              ) : (
                <>
                  <XCircleIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {currentConfig.name} не подключен
                  </span>
                </>
              )}
            </div>
            {estaffMirrorPending && (
              <p className="mt-2 text-sm text-amber-700">
                {estaffMirrorPendingMessage}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <form className="space-y-4" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <div>
            <label htmlFor="ats-source-selector" className="block text-sm font-medium text-gray-700 mb-1">
              ATS
            </label>
            <select
              name="ats-source-selector"
              id="ats-source-selector"
              autoComplete="off"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value as AtsSource)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
            >
              <option value="estaff">E-Staff</option>
              <option value="skillaz">Skillaz</option>
            </select>
          </div>

          <div>
            <label htmlFor="ats-api-endpoint" className="block text-sm font-medium text-gray-700 mb-1">
              Адрес
            </label>
            <input
              type="url"
              name="ats-api-endpoint"
              id="ats-api-endpoint"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="url"
              placeholder="https://example.com"
              value={form.apiUrl}
              onChange={(e) => setForm(prev => ({ ...prev, apiUrl: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
            />
          </div>

          <div>
            <label htmlFor="ats-connection-code" className="block text-sm font-medium text-gray-700 mb-1">
              Токен
            </label>
            <input
              type="text"
              name="ats-connection-code"
              id="ats-connection-code"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={currentConfig.configured ? 'Новый токен для обновления' : 'Введите токен доступа'}
              value={form.apiToken}
              onChange={(e) => setForm(prev => ({ ...prev, apiToken: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 font-mono"
              style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
            />
          </div>

          {selectedSource === 'estaff' && (
            <>
              <div>
                <label htmlFor="estaff-candidate-source" className="block text-sm font-medium text-gray-700 mb-1">
                  Источник
                </label>
                <select
                  name="estaff-candidate-source"
                  id="estaff-candidate-source"
                  autoComplete="off"
                  value={candidateSourceId}
                  onChange={(e) => {
                    setCandidateSourceId(e.target.value);
                    setCandidateSourceTouched(true);
                  }}
                  disabled={!currentConfig.configured || estaffSettingsBlocked || candidateSourcesLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-500"
                  style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
                >
                  {!currentConfig.configured && <option value="">Подключите E-Staff</option>}
                  {estaffSettingsBlocked && <option value="">{estaffSettingsBlockedLabel}</option>}
                  {currentConfig.configured && !estaffSettingsBlocked && candidateSourcesLoading && <option value="">Загрузка...</option>}
                  {currentConfig.configured && !estaffSettingsBlocked && !candidateSourcesLoading && (
                    <option value="">{DEFAULT_CANDIDATE_SOURCE_LABEL}</option>
                  )}
                  {currentConfig.configured && !estaffSettingsBlocked && !hasSelectedSourceOption && candidateSourceId && (
                    <option value={candidateSourceId}>{candidateSourceId}</option>
                  )}
                  {candidateSources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">Статусы по действиям E-Staff</h4>
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  {STATUS_ACTION_ROWS.map((row, index) => {
                    const selectedStatusId = statusMapping[row.key] || '';
                    const hasSelectedStatusOption = selectedStatusId && eventTypeValues.has(selectedStatusId);
                    const statusAction = statusActions.find((action) => action.key === row.key);
                    const selectedStatusLabel = getSelectedStatusLabel(row, selectedStatusId, eventTypes, statusAction);
                    const actionLabel = statusAction?.label || row.label;

                    return (
                      <div
                        key={row.key}
                        className={`grid grid-cols-1 gap-2 p-3 md:grid-cols-[minmax(0,1fr)_minmax(260px,380px)] md:items-center ${index > 0 ? 'border-t border-gray-200' : ''}`}
                      >
                        <div>
                          <label
                            htmlFor={`estaff-status-${row.key}`}
                            className="text-sm font-medium text-gray-800"
                          >
                            {actionLabel}
                          </label>
                          <div className="mt-1 text-xs text-gray-500 break-words">
                            {selectedStatusLabel}
                          </div>
                        </div>
                        <select
                          name={`estaff-status-${row.key}`}
                          id={`estaff-status-${row.key}`}
                          aria-label={`${actionLabel} в E-Staff`}
                          autoComplete="off"
                          value={selectedStatusId}
                          onChange={(e) => setStatusMapping(prev => ({
                            ...prev,
                            [row.key]: row.key === 'aiScreeningPassed' && !e.target.value
                              ? null
                              : e.target.value,
                          }))}
                          disabled={statusSelectDisabled}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm md:text-base focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-500"
                          style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
                        >
                          {!currentConfig.configured && <option value={selectedStatusId}>Подключите E-Staff</option>}
                          {estaffSettingsBlocked && <option value={selectedStatusId}>{estaffSettingsBlockedLabel}</option>}
                          {currentConfig.configured && !estaffSettingsBlocked && eventTypesLoading && <option value={selectedStatusId}>Загрузка...</option>}
                          {currentConfig.configured && !estaffSettingsBlocked && !eventTypesLoading && eventTypes.length === 0 && (
                            <option value={selectedStatusId}>Нет доступных статусов</option>
                          )}
                          {currentConfig.configured && !estaffSettingsBlocked && !eventTypesLoading && eventTypes.length > 0 && row.key !== 'aiScreeningPassed' && !selectedStatusId && (
                            <option value="">Нет выбранного статуса</option>
                          )}
                          {currentConfig.configured && !estaffSettingsBlocked && !eventTypesLoading && row.key === 'aiScreeningPassed' && (
                            <option value="">Не менять статус</option>
                          )}
                          {currentConfig.configured && !estaffSettingsBlocked && !eventTypesLoading && !hasSelectedStatusOption && selectedStatusId && (
                            <option value={selectedStatusId}>{selectedStatusId}</option>
                          )}
                          {eventTypes.map((eventType) => {
                            const optionValue = getEventTypeOptionValue(eventType);
                            return (
                              <option key={`${eventType.id}:${optionValue}`} value={optionValue}>
                                {getStatusOptionLabel(eventType)}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaveDisabled}
              className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => !isSaveDisabled && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
              onMouseLeave={(e) => !isSaveDisabled && (e.currentTarget.style.backgroundColor = colors.primary)}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            {currentConfig.configured && (
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={saving || disconnecting}
                className="px-4 py-2 text-red-600 border border-red-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors"
              >
                {disconnecting ? 'Отключение...' : 'Отключить'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ATSIntegrationContent;
